import { Autocomplete, InputAdornment, Stack, Box, Typography, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent, TextField, DialogActions, FormControl, InputLabel, Select, MenuItem, Divider } from '@mui/material';
import { useState, useMemo } from "react";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import * as React from "react";
import IconButton from "@mui/material/IconButton";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { urls } from "@routes";
import {AddCircleOutline, Delete, Search} from "@mui/icons-material";
import { exportToCSV, CSVColumn, GenericCsvExportButton, gridColDefsToCsvColumns } from "@components/ExportToCSV";

export default function AdminBlockEmails() {
    const [tabValue, setTabValue] = useState<number>(0);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    return (
        <Stack direction="column" spacing={3} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" fontWeight="bold">Manage Blocked Emails</Typography>
            </Box>
            
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="Unsubscribe Emails (Workspace)" />
                    <Tab label="Bad Emails (Global)" />
                </Tabs>
            </Box>

            {tabValue === 0 && <AdminUnsubEmailsTab />}
            {tabValue === 1 && <AdminBadEmailsTab />}
        </Stack>
    );
}


function AdminUnsubEmailsTab() {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const { data, isLoading, refetch } = useQuery({
        queryKey: ["adminUnsubEmails"],
        queryFn: () => authenticateAndFetchData("/admin/unsub-emails/"),
    });

    const unsubEmails = data?.data?.unsubscribe_emails || [];
    const workspaces = data?.data?.workspaces || [];

    // Optimized Filter Logic: Searches Email, Workspace, and Owner
    const filteredUnsubRows = useMemo(() => {
        if (!searchQuery) return unsubEmails;
        const lowQuery = searchQuery.toLowerCase();
        return unsubEmails.filter((row: any) => 
            row.email_id?.toLowerCase().includes(lowQuery) ||
            row.workspace_name?.toLowerCase().includes(lowQuery) ||
            row.user_email?.toLowerCase().includes(lowQuery) ||
            row.user_name?.toLowerCase().includes(lowQuery)
        );
    }, [unsubEmails, searchQuery]);

    const deleteMutation = useMutation({
        mutationFn: (ids: number[]) => authenticateAndPostData("/admin/add-remove-unsub-emails/", {
            action: "remove",
            ids: ids,
        }),
        onSuccess: () => {
            refetch();
            setSelectedRows([]); // Clear selection after delete
            enqueueSnackbar("Emails removed successfully", { variant: "success" });
        }
    });

    const columns: GridColDef[] = [
        { 
            field: "email_id", 
            headerName: "Email Address", 
            flex: 1.5,
            renderCell: (params) => (
                <Box display="flex" alignItems="center" height="100%">
                    {params.value}
                </Box>
            )
        },
        { 
            field: "workspace_name", 
            headerName: "Workspace", 
            flex: 1, 
            renderCell: (params) => (
                <Box 
                    display="flex" 
                    alignItems="center" 
                    height="100%"
                    onClick={() => {
                        if (params.row.user_id) {
                            navigate(urls["adminUserWorkspaces"].replace(":userId", params.row.user_id));
                        }
                    }}
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                    {params.row.workspace_name || "N/A"}
                </Box>
            )
        },
        { 
            field: "user_email", 
            headerName: "User Email", 
            flex: 1.2,
            renderCell: (params) => (
                <Box 
                    display="flex" 
                    flexDirection="column" 
                    justifyContent="center" 
                    height="100%" 
                    onClick={() => {
                        if (params.row.user_id) {
                            navigate(urls["adminUserDetails"].replace(":userId", params.row.user_id));
                        }
                    }}
                    sx={{ cursor: 'pointer' }}
                >
                    <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                        {params.row.user_name || 'No Name'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                        {params.row.user_email}
                    </Typography>
                </Box>
            )
        },
        {
            field: "added_on",
            headerName: "Date Added",
            width: 180,           
            valueGetter: (value, row) => {
                const rawValue = value ?? row?.added_on;
                if (!rawValue) return null;
                const date = new Date(rawValue);
                return isNaN(date.getTime()) ? null : date;
            },
            renderCell: (params) => (
                <Box display="flex" alignItems="center" height="100%">
                    <Typography variant="body2">
                        {params.row.added_on ? new Date(params.row.added_on).toLocaleDateString() : ""}
                    </Typography>
                </Box>
            )
        },
        {
            field: "actions",
            headerName: "Action",
            width: 80,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <IconButton color="error" size="small" onClick={() => deleteMutation.mutate([params.row.id])}>
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            )
        }
    ];

    return (
        <Stack spacing={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center">
                    <Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => setOpenDialog(true)}>
                        Add Unsubscribe Emails
                    </Button>
                    <GenericCsvExportButton
                        endpoint="/admin/unsub-emails/"
                        queryParams={{
                            search: searchQuery,
                        }}
                        columns={gridColDefsToCsvColumns(columns)}
                        exportName={"UnsubscribedEmails"}
                        transformResponse={(res) => res?.data?.unsubscribe_emails || []}
                    />
                    <TextField
                        size="small"
                        placeholder="Search email, workspace, or owner..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ width: 350 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Stack>
                {selectedRows.length > 0 && (
                    <Button color="error" variant="contained" onClick={() => deleteMutation.mutate(selectedRows)}>
                        Delete Selected ({selectedRows.length})
                    </Button>
                )}
            </Box>
            
            <DataGrid
                rows={filteredUnsubRows}
                columns={columns}
                loading={isLoading}
                checkboxSelection
                onRowSelectionModelChange={(ids) => setSelectedRows(ids as number[])}
                autoHeight
                getRowHeight={() => 'auto'} // Allows the multi-line Owner cell to fit
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                    pagination: { paginationModel: { page: 0, pageSize: 10 } }
                }}
                sx={{ 
                    mt: 1,
                    '& .MuiDataGrid-cell': { py: 1 }
                }}
            />

            <AddUnsubscribeEmailDialog 
                enqueueSnackbar={enqueueSnackbar}
                open={openDialog} 
                workspaces={workspaces}
                onClose={() => setOpenDialog(false)} 
                onSuccess={() => { setOpenDialog(false); refetch(); }} 
            />
        </Stack>
    );
}

// --- Tab 2: Bad Emails (Global) ---
function AdminBadEmailsTab() {
    const { enqueueSnackbar } = useSnackbar();
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const [openDialog, setOpenDialog] = useState(false);
    const [emailInput, setEmailInput] = useState("");
    const [reasonInput, setReasonInput] = useState("");
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    
    const { data, isLoading, refetch } = useQuery({
        queryKey: [
            "adminBadEmails", 
            paginationModel.page + 1, 
            paginationModel.pageSize, 
            searchQuery
        ],
        queryFn: () => authenticateAndFetchData(
            `/admin/bad-emails/?page=${paginationModel.page + 1}&page_size=${paginationModel.pageSize}&search=${encodeURIComponent(searchQuery)}`
        ),
    });

    const rows = data?.data?.data || [];
    const totalCount = data?.data?.total_count || 0;

    const filteredRows = useMemo(() => {
        const rawRows = data?.data?.data || [];
        if (!searchQuery) return rawRows;
        
        const lowerQuery = searchQuery.toLowerCase();
        return rawRows.filter((row: any) => 
            row.email_id?.toLowerCase().includes(lowerQuery) ||
            row.reason?.toLowerCase().includes(lowerQuery)
        );
    }, [data, searchQuery]);

    const allLines = emailInput.split('\n').map(e => e.trim()).filter(e => e !== "");
    const validEmails = allLines.filter(email => EMAIL_REGEX.test(email));
    const invalidEmails = allLines.filter(email => !EMAIL_REGEX.test(email));


    const mutation = useMutation({
        mutationFn: (payload: any) => authenticateAndPostData("/admin/add-remove-bad-emails/", payload),
        onSuccess: (res) => {
            refetch();
            setEmailInput("");
            setReasonInput("");
            setSelectedRows([]);
            setOpenDialog(false);
            enqueueSnackbar(res.data.message, { variant: "success" });
        }
    });


    const columns: GridColDef[] = [
        { field: "email_id", headerName: "Global Blocked Email", flex: 1 },
        { field: "reason", headerName: "Reason", flex: 1 },
        {
            field: "actions",
            headerName: "Action",
            width: 80,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <IconButton 
                    color="error" 
                    size="small"
                    onClick={() => mutation.mutate({ action: 'remove', emails: [params.row.email_id] })}
                >
                    <Delete fontSize="small" />
                </IconButton>
            )
        }
    ];

    // Map MUI GridColDef to CSV export definition
    const csvColumns = useMemo(() => gridColDefsToCsvColumns(columns), [columns]);

    return (
        <Stack spacing={3}>            
            <Box display="flex" justifyContent="space-between" alignItems="center">                
                <Stack direction="row" spacing={2}>
                    <Button 
                        variant="contained" 
                        startIcon={<AddCircleOutline />} 
                        onClick={() => setOpenDialog(true)}
                    >
                        Add Bad Emails
                    </Button>
                    <GenericCsvExportButton
                        endpoint="/admin/bad-emails/"
                        queryParams={{
                            search: searchQuery,
                            export_all: "true"
                        }}
                        columns={csvColumns}
                        exportName={`GlobalBadEmails`}
                        transformResponse={(res) => res?.data?.data || []}
                    />                    
                    <TextField
                        size="small"
                        placeholder="Search blocked emails..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                        sx={{ width: 300 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Stack>
                {selectedRows.length > 0 && (
                    <Button 
                        variant="outlined" 
                        color="error" 
                        startIcon={<Delete />}
                        onClick={() => mutation.mutate({ action: 'remove', emails: selectedRows })}
                    >
                        Remove Selected ({selectedRows.length})
                    </Button>
                )}
            </Box>

            <DataGrid 
                rows={rows}
                columns={columns} 
                loading={isLoading} 
                autoHeight 
                checkboxSelection                
                paginationMode="server"
                rowCount={totalCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}

                onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection as string[])}
                getRowId={(row) => row.email_id}
                localeText={{ noRowsLabel: "No Bad Emails Found" }}
                sx={{ mt: 2 }}
            />

            {/* Bulk Add Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>Bulk Add Global Blocks</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField 
                            fullWidth 
                            label="Emails (One per line)" 
                            placeholder="user1@domain.com&#10;user2@domain.com"
                            variant="outlined"
                            multiline
                            rows={6}
                            value={emailInput} 
                            onChange={(e) => setEmailInput(e.target.value)}
                            // Visual feedback: error state if there are invalid emails
                            error={invalidEmails.length > 0}
                            helperText={
                                invalidEmails.length > 0 
                                ? `${invalidEmails.length} invalid email(s) detected and will be skipped.` 
                                : `Ready to add ${validEmails.length} valid email(s).`
                            }
                        />
                        
                        {invalidEmails.length > 0 && (
                            <Box sx={{ p: 1.5, bgcolor: '#fff5f5', borderRadius: 1, border: '1px solid #feb2b2' }}>
                                <Typography variant="caption" color="error" fontWeight="bold">
                                    Invalid formats (will be ignored):
                                </Typography>
                                <Typography variant="caption" component="div" color="error">
                                    {invalidEmails.slice(0, 5).join(", ")} {invalidEmails.length > 5 && "..."}
                                </Typography>
                            </Box>
                        )}

                        <TextField 
                            fullWidth 
                            label="Reason for Blocking" 
                            variant="outlined"
                            value={reasonInput} 
                            onChange={(e) => setReasonInput(e.target.value)} 
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDialog(false)} color="inherit">Cancel</Button>
                    <Button 
                        variant="contained" 
                        color="error"
                        // Disable if no valid emails exist or mutation is running
                        disabled={validEmails.length === 0 || mutation.isPending}
                        onClick={() => mutation.mutate({ 
                            action: 'add', 
                            emails: validEmails, // Only send the valid ones
                            reason: reasonInput 
                        })}
                    >
                        {mutation.isPending ? "Processing..." : `Block ${validEmails.length} Email(s)`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

// --- Add Unsubscribe Emails ---
function AddUnsubscribeEmailDialog({ enqueueSnackbar, open, onClose, onSuccess, workspaces }: any) {
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    const [emailText, setEmailText] = useState("");
    const [selectedUser, setSelectedUser] = useState<any | null>(null);
    const [selectedWorkspace, setSelectedWorkspace] = useState<any | null>(null);

    const { data: userData } = useQuery({
        queryKey: ["adminUsers"],
        queryFn: () => authenticateAndFetchData("/admin/get-users/"),
        enabled: open // Only fetch when dialog opens
    });
    const users = userData?.data?.users || [];

    // Filter workspaces based on selected user
    const filteredWorkspaces = useMemo(() => {
        if (!selectedUser) return [];
        return workspaces.filter((ws: any) => ws.user__id === selectedUser.id);
    }, [selectedUser, workspaces]);

    // Validation logic
    const allLines = emailText.split("\n").map(e => e.trim()).filter(e => e !== "");
    const validEmails = allLines.filter(email => EMAIL_REGEX.test(email));
    const invalidEmails = allLines.filter(email => !EMAIL_REGEX.test(email));

    const mutation = useMutation({
        mutationFn: (payload: any) => authenticateAndPostData("/admin/add-remove-unsub-emails/", payload),
        onSuccess: (res) => {
            enqueueSnackbar(res.data.message, { variant: "success" });
            onSuccess();
            handleClose();
        }
    });

    const handleClose = () => {
        setEmailText("");
        setSelectedUser(null);
        setSelectedWorkspace(null);
        onClose();
    };

    const handleSubmit = () => {
        mutation.mutate({
            action: "add",
            workspace_id: selectedWorkspace?.id,
            emails: validEmails
        });
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Add Unsubscribe Emails</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>                   
                    <Autocomplete
                        options={users}                       
                        getOptionLabel={(option) => `${option.username || 'No Username'} (${option.email})`}
                        value={selectedUser}
                        onChange={(_, newValue) => {
                            setSelectedUser(newValue);
                            setSelectedWorkspace(null);
                        }}                        
                        renderOption={(props, option) => (
                            <li {...props}>
                                <Box>
                                    <Typography variant="body1">
                                        {option.username || 'No Username'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {option.email}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} label="Select User" variant="outlined" />
                        )}
                    />
                    
                    <Autocomplete
                        options={filteredWorkspaces}
                        disabled={!selectedUser}
                        getOptionLabel={(option) => 
                            option.id === option.user__active_workspace 
                                ? `${option.name} (Active)` 
                                : option.name
                        }
                        value={selectedWorkspace}
                        onChange={(_, newValue) => setSelectedWorkspace(newValue)}
                        renderOption={(props, option) => (
                            <li {...props}>
                                <Typography variant="body1">
                                    {option.name} 
                                    {option.id === option.user__active_workspace && (
                                        <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                                            (Active)
                                        </Box>
                                    )}
                                </Typography>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField 
                                {...params}
                                label={selectedUser ? "Select Workspace" : "Select a user first"} 
                                variant="outlined" 
                            />
                        )}
                    />

                    {/* 3. EMAIL INPUT */}
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="3. Emails (One per line)"
                        value={emailText}
                        onChange={(e) => setEmailText(e.target.value)}
                        error={invalidEmails.length > 0}
                        helperText={
                            invalidEmails.length > 0 
                            ? `${invalidEmails.length} invalid email(s) detected.` 
                            : ""
                        }
                    />
                </Stack>
                
                {/* Validation Display */}
                {invalidEmails.length > 0 && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                        <Typography variant="caption" color="white">
                            Ignoring: {invalidEmails.slice(0, 2).join(", ")}...
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} color="inherit">Cancel</Button>
                <Button 
                    variant="contained" 
                    disabled={!selectedWorkspace || validEmails.length === 0 || mutation.isPending}
                    onClick={handleSubmit}
                >
                    {mutation.isPending ? "Adding..." : `Add ${validEmails.length} Emails`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}